import React, { useState, useEffect } from 'react';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import Checkbox from '@material-ui/core/Checkbox';
import ListItemText from '@material-ui/core/ListItemText';
import TextField from '@material-ui/core/TextField';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { API } from 'aws-amplify';
import { getAPIName } from '../config';
import { FormControl, Chip } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { renderDealershipOptions, renderDealershipGroupOptions } from './renderDealershipOptions';

interface DealershipSelectProps {
    dealershipId?: number | number[] | undefined;
    setDealershipId?: (id: any) => void;
    dealershipGroupId?: number | number[] | undefined;
    setDealershipGroupId?: (id: any) => void;
    searchType: string | unknown;
    setSearchType: (id: string | unknown) => void;
    inputProps?: { color?: string; backgroundColor?: string };
    enableAutocomplete?: boolean;
    enableMultiSelect?: boolean;
}
interface DealershipOption {
    id: number;
    name: string;
}

const DealershipSelect: React.FC<DealershipSelectProps> = ({
    dealershipId,
    setDealershipId,
    dealershipGroupId,
    setDealershipGroupId,
    searchType,
    setSearchType,
    inputProps,
    enableAutocomplete = false,
    enableMultiSelect = false,
}) => {
    const useStyles = makeStyles((theme) => ({
        formControl: {
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: 10,
            width: '95%',
            gap: 20,
            [theme.breakpoints.down('sm')]: {
            flexDirection: 'column',
            '& > *': { width: '100% !important', marginBottom: 10 },
            },
        },
        select: {
            color: inputProps?.color ?? 'white',
            backgroundColor: inputProps?.backgroundColor ?? '#6f7179',
            borderRadius: 6,
            padding: '0 8px 0px 15px',
            height: 37,
            display: 'flex',
            alignItems: 'center',
            '&:hover, &.Mui-focused': {
            backgroundColor: inputProps?.backgroundColor ?? '#6f7179',
            },
        },
        autocomplete: {
            width: '55%',
            position: 'relative',
            '& .MuiInputBase-root': {
                color: inputProps?.color ?? 'white',
                backgroundColor: inputProps?.backgroundColor ?? '#6f7179',
                borderRadius: 6,
                minHeight: 40,
                boxSizing: 'border-box',
                overflow: 'visible',
                padding: '4px 9px !important',
            },
            '& .MuiAutocomplete-inputRoot': {
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                maxHeight: 120,
                overflowY: 'auto',
                paddingRight: 48,    
                boxSizing: 'border-box',
                '&::-webkit-scrollbar': { width: 6 },
                '&::-webkit-scrollbar-thumb': { background: 'rgba(255,255,255,0.3)', borderRadius: 3 },
            },
            '& .MuiAutocomplete-input': {
                padding: '4px 4px !important',
                color: inputProps?.color ?? 'white',
                minWidth: '30px !important',
                '&::placeholder': { color: inputProps?.color ?? 'white !important', opacity: 1, transition: 'color .12s ease' },
            },
            '& .MuiInputBase-root.Mui-focused .MuiAutocomplete-input::placeholder': {
                color: inputProps?.color ?? 'transparent !important',
            },
            '& .MuiAutocomplete-inputRoot.Mui-focused .MuiAutocomplete-input::placeholder': {
                color: inputProps?.color ??'transparent !important',
            },
            '& .MuiAutocomplete-tag': { backgroundColor: '#4a4d57', margin: 2, height: 24 },
            '& .MuiAutocomplete-listbox': { maxHeight: '40vh', overflowY: 'auto' },
            '& .MuiAutocomplete-endAdornment': {
                position: 'sticky',
                right: 8,
                alignSelf: 'center',
                top: 'auto',
                zIndex: 2,
                display: 'flex',
                alignItems: 'center',
                background: 'inherit',   
                marginLeft: 'auto'
            },
            '& .MuiAutocomplete-clearIndicator, & .MuiAutocomplete-popupIndicator': {
                pointerEvents: 'auto',
                padding: 4,
                marginLeft: 4,
                background: 'transparent',
            },
            '& .MuiOutlinedInput-notchedOutline, & .MuiInput-underline:before, & .MuiInput-underline:after': {
                display: 'none !important',
                border: 'none !important',
            },
        },
        chip: {
            color: 'white',
            backgroundColor: '#4a4d57',
            textAlign: 'center',
            margin: 3,
            maxWidth: 'calc(100% - 6px)',
        },
    }));
    const classes = useStyles();
    const [dealershipOptions, setDealershipOptions] = useState<DealershipOption[]>([]);
    const handleDealershipOptionsDropdown = (e: any) => {
        if (enableMultiSelect) {
            const value = e.target.value;
            if (searchType === 'dealership' && setDealershipId) {
                setDealershipId(typeof value === 'string' ? value.split(',').map(Number) : value);
            }
            if (searchType === 'dealershipGroup' && setDealershipGroupId) {
                setDealershipGroupId(typeof value === 'string' ? value.split(',').map(Number) : value);
            }
        } else {
            if (searchType === 'dealership' && setDealershipId) {
                setDealershipId(e.target.value.toString());
            }
            if (searchType === 'dealershipGroup' && setDealershipGroupId) {
                setDealershipGroupId(e.target.value.toString());
            }
        }
    };

    const handleAutocompleteChange = (event: any, value: DealershipOption | DealershipOption[] | null) => {
        if (enableMultiSelect && Array.isArray(value)) {
            const ids = value.map((option) => option.id);
            if (searchType === 'dealership' && setDealershipId) {
                setDealershipId(ids);
            }
            if (searchType === 'dealershipGroup' && setDealershipGroupId) {
                setDealershipGroupId(ids);
            }
        } else if (!enableMultiSelect && value && !Array.isArray(value)) {
            if (searchType === 'dealership' && setDealershipId) {
                setDealershipId(value.id);
            }
            if (searchType === 'dealershipGroup' && setDealershipGroupId) {
                setDealershipGroupId(value.id);
            }
        } else {
            if (searchType === 'dealership' && setDealershipId) {
                setDealershipId(undefined);
            }
            if (searchType === 'dealershipGroup' && setDealershipGroupId) {
                setDealershipGroupId(undefined);
            }
        }
        console.log(dealershipId)
        console.log(dealershipGroupId)
    };

    const getDealerships = async () => {
        try {
            let apiName = getAPIName();
            let path = '/dealerships?orderBy-=name';
            let myInit = {};
            const response = await API.get(apiName, path, myInit);
            const results = response.body;
            setDealershipOptions(results);
        } catch (error) {
            console.log(error);
        }
    };
    const getDealershipGroups = async () => {
        try {
            let apiName = getAPIName();
            let path = '/dealershipgroups?orderBy-=name';
            let myInit = {};
            const response = await API.get(apiName, path, myInit);
            const results = response.body;
            setDealershipOptions(results);
        } catch (error) {
            console.log(error);
        }
    };

    useEffect(() => {
        getDealerships();
    }, []);

    const handleSearchTypeChange = (e: React.ChangeEvent<{ value: string | unknown }>) => {
        let type = e.target.value;
        setSearchType(type);
        if (type === 'dealership') {
            getDealerships();
        } else if (type === 'dealershipGroup') {
            getDealershipGroups();
        }
    };

    // Get selected value(s) for autocomplete
    const getAutocompleteValue = () => {
        const currentId = searchType === 'dealership' ? dealershipId : dealershipGroupId;
        
        if (enableMultiSelect && Array.isArray(currentId)) {
            return dealershipOptions.filter((option) => currentId.includes(option.id));
        } else if (!enableMultiSelect && typeof currentId === 'number') {
            return dealershipOptions.find((option) => option.id === currentId) || null;
        }
        return enableMultiSelect ? [] : null;
    };

    const handleSelectAll = () => {
        const allIds = dealershipOptions.map((o) => o.id);
        if (searchType === 'dealership' && setDealershipId) setDealershipId(allIds);
        if (searchType === 'dealershipGroup' && setDealershipGroupId) setDealershipGroupId(allIds);
    };


    return (
        <>
            <FormControl className={classes.formControl}>
                <Select
                    value={searchType}
                    onChange={handleSearchTypeChange}
                    autoWidth={true}
                    displayEmpty
                    style={{ width: '35%' }}
                    inputProps={{
                        classes: {
                            root: classes.select,
                        },
                    }}
                >
                    <MenuItem key="0" value={''}>
                        Select Search Type
                    </MenuItem>
                    <MenuItem key={'1'} value={'dealership'}>
                        Location
                    </MenuItem>
                    <MenuItem key={'2'} value={'dealershipGroup'}>
                        Group
                    </MenuItem>
                </Select>

                {searchType === 'dealership' && enableAutocomplete && (
                    <Autocomplete
                        multiple={enableMultiSelect}
                        options={
                            enableMultiSelect
                                ? [{ id: -1, name: 'Select All' }, ...dealershipOptions]
                                : dealershipOptions
                        }
                        getOptionLabel={(option) => option.name}
                        value={getAutocompleteValue()}
                        onChange={(e, value) => {
                            if (enableMultiSelect && Array.isArray(value)) {
                                // handle Select All
                                if (value.some((v) => v.id === -1)) {
                                handleSelectAll();
                                } else handleAutocompleteChange(e, value);
                            } else handleAutocompleteChange(e, value);
                        }}
                        style={{ width: '55%' }}
                        className={classes.autocomplete}
                        renderOption={(option) => (
                            <>
                                {option.id === -1 ? (
                                <span style={{ fontWeight: 600 }}>Select All</span>
                                ) : (
                                option.name
                                )}
                            </>
                        )}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                placeholder={enableMultiSelect ? 'Select Locations' : 'Select Location'}
                            />
                        )}
                        renderTags={
                            enableMultiSelect
                                ? (value, getTagProps) =>
                                      value.map((option, index) => (
                                          <Chip
                                              label={option.name}
                                              {...getTagProps({ index })}
                                              className={classes.chip}
                                          />
                                      ))
                                : undefined
                        }
                    />
                )}

                {searchType === 'dealership' && !enableAutocomplete && (
                    <Select
                        style={{ width: '55%' }}
                        value={dealershipId}
                        onChange={handleDealershipOptionsDropdown}
                        autoWidth={true}
                        displayEmpty
                        multiple={enableMultiSelect}
                        renderValue={
                            enableMultiSelect
                                ? (selected) => {
                                      const selectedArray = selected as number[];
                                      return dealershipOptions
                                          .filter((option) => selectedArray.includes(option.id))
                                          .map((option) => option.name)
                                          .join(', ');
                                  }
                                : undefined
                        }
                        inputProps={{
                            classes: {
                                root: classes.select,
                            },
                        }}
                    >
                        {enableMultiSelect
                            ? dealershipOptions.map((option) => (
                                  <MenuItem key={option.id} value={option.id}>
                                      <Checkbox
                                          checked={
                                              Array.isArray(dealershipId) && dealershipId.indexOf(option.id) > -1
                                          }
                                      />
                                      <ListItemText primary={option.name} />
                                  </MenuItem>
                              ))
                            : dealershipOptions && renderDealershipOptions(dealershipOptions)}
                    </Select>
                )}

                {searchType === 'dealershipGroup' && enableAutocomplete && (
                    <Autocomplete
                        multiple={enableMultiSelect}
                        style={{ width: '55%' }}
                        options={
                            enableMultiSelect
                                ? [{ id: -1, name: 'Select All' }, ...dealershipOptions]
                                : dealershipOptions
                        }
                        getOptionLabel={(option) => option.name}
                        value={getAutocompleteValue()}
                        onChange={(e, value) => {
                            if (enableMultiSelect && Array.isArray(value)) {
                                // handle Select All
                                if (value.some((v) => v.id === -1)) {
                                handleSelectAll();
                                } else handleAutocompleteChange(e, value);
                            } else handleAutocompleteChange(e, value);
                        }}
                        className={classes.autocomplete}
                        renderOption={(option) => (
                            <>
                                {option.id === -1 ? (
                                <span style={{ fontWeight: 600 }}>Select All</span>
                                ) : (
                                option.name
                                )}
                            </>
                        )}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                placeholder={enableMultiSelect ? 'Select Groups' : 'Select Group'}
                            />
                        )}
                        renderTags={
                            enableMultiSelect
                                ? (value, getTagProps) =>
                                      value.map((option, index) => (
                                          <Chip
                                              label={option.name}
                                              {...getTagProps({ index })}
                                              className={classes.chip}
                                          />
                                      ))
                                : undefined
                        }
                    />
                )}

                {searchType === 'dealershipGroup' && !enableAutocomplete && (
                    <Select
                        style={{ width: '55%' }}
                        value={dealershipGroupId}
                        onChange={handleDealershipOptionsDropdown}
                        autoWidth={true}
                        displayEmpty
                        multiple={enableMultiSelect}
                        renderValue={
                            enableMultiSelect
                                ? (selected) => {
                                      const selectedArray = selected as number[];
                                      return dealershipOptions
                                          .filter((option) => selectedArray.includes(option.id))
                                          .map((option) => option.name)
                                          .join(', ');
                                  }
                                : undefined
                        }
                        inputProps={{
                            classes: {
                                root: classes.select,
                            },
                        }}
                    >
                        {enableMultiSelect
                            ? dealershipOptions.map((option) => (
                                  <MenuItem key={option.id} value={option.id}>
                                      <Checkbox
                                          checked={
                                              Array.isArray(dealershipGroupId) &&
                                              dealershipGroupId.indexOf(option.id) > -1
                                          }
                                      />
                                      <ListItemText primary={option.name} />
                                  </MenuItem>
                              ))
                            : dealershipOptions && renderDealershipGroupOptions(dealershipOptions)}
                    </Select>
                )}
            </FormControl>
        </>
    );
};

export default DealershipSelect;